import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";

export interface WhitelistEntry {
  character_id: number;
  character_name: string;
}

export interface AccessPolicy {
  allowed_corporation_id: number | null;
  allowed_corporation_name: string | null;
  allowed_alliance_id: number | null;
  allowed_alliance_name: string | null;
  whitelist: WhitelistEntry[];
}

export interface AmbiguousCandidate {
  id: number;
  name: string;
}

export interface AmbiguousResponse {
  error: string;
  code: "ambiguous_name";
  candidates: AmbiguousCandidate[];
}

const KEY = ["admin", "access"] as const;

export function useAccessPolicy() {
  return useQuery({
    queryKey: KEY,
    queryFn: () => api<AccessPolicy>("/admin/access"),
  });
}

export function useSetCorporation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      name?: string;
      corporation_id?: number;
      corporation_name?: string;
    }) =>
      api<AccessPolicy>("/admin/access/corporation", {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useClearCorporation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      api<{ ok: boolean }>("/admin/access/corporation", { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useSetAlliance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      name?: string;
      alliance_id?: number;
      alliance_name?: string;
    }) =>
      api<AccessPolicy>("/admin/access/alliance", {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useClearAlliance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      api<{ ok: boolean }>("/admin/access/alliance", { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useAddWhitelist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      name?: string;
      character_id?: number;
      character_name?: string;
    }) =>
      api<WhitelistEntry>("/admin/access/whitelist", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useRemoveWhitelist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (characterId: number) =>
      api<{ ok: boolean }>(`/admin/access/whitelist/${characterId}`, {
        method: "DELETE",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
